import React, { useEffect, useState } from "react";

import { usersService } from "api/services/users.service";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";

import type { UserDetail } from "api/types/user.types";
import "./UserContactEditModal.scss";

interface UserContactEditModalProps {
  open: boolean;
  userId: string | number; // public_id(UUID) 또는 정수 PK — 백엔드 dual-lookup 지원
  initialEmail: string | null;
  initialHp: string | null;
  onClose: () => void;
  onSaved: (user: UserDetail) => void;
}

const EMAIL_MAX = 50;
const HP_MAX = 12;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 백엔드 필드 에러({ hp: [...], user_email: [...] })를 사용자 메시지로 변환 */
const parseFieldErrors = (data: unknown): { email?: string; hp?: string; general?: string } => {
  if (!data || typeof data !== "object") return { general: "변경에 실패했습니다." };
  const errors = data as Record<string, unknown>;
  const first = (v: unknown): string | undefined => (Array.isArray(v) ? String(v[0]) : typeof v === "string" ? v : undefined);
  return {
    email: first(errors.user_email),
    hp: first(errors.hp),
    general: first(errors.message) ?? first(errors.detail),
  };
};

const UserContactEditModal: React.FC<UserContactEditModalProps> = ({ open, userId, initialEmail, initialHp, onClose, onSaved }) => {
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [emailError, setEmailError] = useState<string>();
  const [hpError, setHpError] = useState<string>();
  const [generalError, setGeneralError] = useState<string>();
  const [saving, setSaving] = useState(false);

  // 모달이 열릴 때마다 현재 값으로 초기화
  useEffect(() => {
    if (open) {
      setEmail(initialEmail ?? "");
      setHp(initialHp ?? "");
      setEmailError(undefined);
      setHpError(undefined);
      setGeneralError(undefined);
    }
  }, [open, initialEmail, initialHp]);

  const validate = (): boolean => {
    let ok = true;
    const trimmedEmail = email.trim();
    const trimmedHp = hp.trim();

    if (!trimmedEmail) {
      setEmailError("이메일을 입력해주세요.");
      ok = false;
    } else if (trimmedEmail.length > EMAIL_MAX) {
      setEmailError(`이메일은 최대 ${EMAIL_MAX}자까지 입력할 수 있습니다.`);
      ok = false;
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError("올바른 이메일 형식이 아닙니다.");
      ok = false;
    } else {
      setEmailError(undefined);
    }

    if (!trimmedHp) {
      setHpError("휴대폰 번호를 입력해주세요.");
      ok = false;
    } else if (!/^\d+$/.test(trimmedHp)) {
      setHpError("휴대폰 번호는 숫자만 입력해주세요.");
      ok = false;
    } else if (trimmedHp.length > HP_MAX) {
      setHpError(`휴대폰 번호는 최대 ${HP_MAX}자까지 입력할 수 있습니다.`);
      ok = false;
    } else {
      setHpError(undefined);
    }

    return ok;
  };

  const handleSave = async () => {
    setGeneralError(undefined);
    if (!validate()) return;

    const trimmedEmail = email.trim();
    const trimmedHp = hp.trim();

    // 변경된 필드만 전송
    const payload: { user_email?: string; hp?: string } = {};
    if (trimmedEmail !== (initialEmail ?? "")) payload.user_email = trimmedEmail;
    if (trimmedHp !== (initialHp ?? "")) payload.hp = trimmedHp;

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const res = await usersService.adminManage(userId, payload);
      onSaved(res.data);
      onClose();
    } catch (err) {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      const parsed = parseFieldErrors(data);
      setEmailError(parsed.email);
      setHpError(parsed.hp);
      setGeneralError(parsed.general ?? (!parsed.email && !parsed.hp ? "변경에 실패했습니다." : undefined));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="연락처 정보 수정"
      width="440px"
      onClose={saving ? undefined : onClose}
      closeOnOverlayClick={!saving}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? "저장 중..." : "저장"}
          </Button>
        </>
      }
    >
      <div className="user-contact-edit">
        <Input label="이메일" type="email" value={email} maxLength={EMAIL_MAX} placeholder="example@buccl.com" onChange={(e) => setEmail(e.target.value)} error={emailError} disabled={saving} />
        <Input label="휴대폰 번호" type="tel" inputMode="numeric" value={hp} maxLength={HP_MAX} placeholder="01012345678 (- 없이 숫자만)" onChange={(e) => setHp(e.target.value.replace(/[^\d]/g, ""))} error={hpError} disabled={saving} />
        <p className="user-contact-edit__notice">이메일과 휴대폰 번호는 탈퇴한 회원을 포함한 전체 사용자 기준으로 중복될 수 없습니다.</p>
        {generalError && <p className="user-contact-edit__error">{generalError}</p>}
      </div>
    </Modal>
  );
};

export default UserContactEditModal;
